import React, { useEffect, useId, useMemo, useState } from 'react'

import { ChartView, formatValue } from './ChartView'

/**
 * The country comparison chart: the countries a reader picks, on one World
 * Bank measure at a time. The page sends every country's figure for each
 * measure (`chart.measures`, from Chart#lookup_data), so nothing more is
 * fetched. It opens with the countries in the page's own data (`chart.data`),
 * on the first measure. `?measure=population-by-country&country=Japan&country=Brazil`
 * opens a comparison directly, and the address follows each change so a
 * comparison can be shared.
 */

const MAX_COUNTRIES = 12

export function CountryCompare({ chart }) {
  const measureId = useId()
  const inputId = useId()
  const listId = useId()
  const measures = useMemo(() => (Array.isArray(chart.measures) ? chart.measures : []), [chart.measures])
  const allCountries = useMemo(
    () => [...new Set(measures.flatMap((m) => m.data.map((p) => p.label)))].sort((a, b) => a.localeCompare(b)),
    [measures]
  )
  const [measureSlug, setMeasureSlug] = useState(measures[0]?.slug)
  const [countries, setCountries] = useState(() => (chart.data ?? []).map((p) => p.label))
  const [query, setQuery] = useState('')
  const [message, setMessage] = useState(null)

  const findCountry = (name) => {
    const needle = name.trim().toLowerCase()
    if (!needle) return null
    const exact = allCountries.find((c) => c.toLowerCase() === needle)
    if (exact) return exact
    const matches = allCountries.filter((c) => c.toLowerCase().includes(needle))
    return matches.length === 1 ? matches[0] : null
  }

  const updateUrl = (nextMeasure, nextCountries) => {
    const url = new URL(window.location.href)
    url.searchParams.set('measure', nextMeasure)
    url.searchParams.delete('country')
    nextCountries.forEach((c) => url.searchParams.append('country', c))
    window.history.replaceState(null, '', url)
  }

  // Open the comparison in the address, if any.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const wanted = [...new Set(params.getAll('country').map(findCountry).filter(Boolean))].slice(0, MAX_COUNTRIES)
    if (wanted.length) setCountries(wanted)
    if (measures.some((m) => m.slug === params.get('measure'))) setMeasureSlug(params.get('measure'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (measures.length === 0) return <ChartView chart={chart} />

  const measure = measures.find((m) => m.slug === measureSlug) ?? measures[0]
  const values = new Map(measure.data.map((p) => [p.label, p.value]))
  const points = countries
    .filter((c) => values.has(c))
    .map((c) => ({ label: c, value: values.get(c) }))
    .sort((a, b) => b.value - a.value)
  const missing = countries.filter((c) => !values.has(c))

  const chooseMeasure = (slug) => {
    setMeasureSlug(slug)
    updateUrl(slug, countries)
  }

  const setAndShare = (next) => {
    setCountries(next)
    updateUrl(measure.slug, next)
  }

  const add = () => {
    const found = findCountry(query)
    if (!found) {
      setMessage(`No country matches “${query.trim()}”. Pick one from the list as you type.`)
    } else if (countries.includes(found)) {
      setMessage(`${found} is already in the comparison.`)
    } else if (countries.length >= MAX_COUNTRIES) {
      setMessage(`Compare up to ${MAX_COUNTRIES} countries at a time. Remove one to add ${found}.`)
    } else {
      setAndShare([...countries, found])
      setQuery('')
      setMessage(null)
    }
  }

  return (
    <div className="w-full">
      <form
        className="mb-4 grid gap-3 sm:grid-cols-2 sm:items-end"
        onSubmit={(event) => { event.preventDefault(); add() }}
      >
        <div className="min-w-0">
          <label htmlFor={measureId} className="form-label">Compare on</label>
          <select
            id={measureId}
            className="input-form-field min-h-10"
            value={measure.slug}
            onChange={(event) => chooseMeasure(event.target.value)}
          >
            {measures.map((m) => <option key={m.slug} value={m.slug}>{m.name}</option>)}
          </select>
        </div>
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <label htmlFor={inputId} className="form-label">Add a country</label>
            <input
              id={inputId}
              type="search"
              className="input-form-field"
              placeholder="Type a country, e.g. Mexico"
              autoComplete="off"
              list={listId}
              value={query}
              maxLength={60}
              onChange={(event) => setQuery(event.target.value)}
            />
            <datalist id={listId}>
              {allCountries.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <button type="submit" className="btn-primary min-h-10">Add</button>
        </div>
      </form>

      {message ? <p className="alert-info mb-4" role="status">{message}</p> : null}

      <ul className="mb-4 flex flex-wrap gap-2" aria-label="Countries in the comparison">
        {countries.map((c) => (
          <li key={c}>
            <button
              type="button"
              className="btn-secondary btn-sm min-h-10 max-w-full"
              aria-label={`Remove ${c}`}
              onClick={() => setAndShare(countries.filter((other) => other !== c))}
            >
              <span className="truncate">{c}</span> <span aria-hidden="true">×</span>
            </button>
          </li>
        ))}
      </ul>

      {points.length ? (
        <ChartView
          searchable={false}
          chart={{ ...chart, title: measure.title, y_label: `${measure.y_label}, ${measure.year}`, data: points }}
        />
      ) : (
        <p className="text-sm text-ink-muted">Add a country to compare.</p>
      )}

      {missing.length ? (
        <p className="mt-3 text-sm text-ink-muted break-words">
          No {measure.year} figure for {missing.join(', ')}.
        </p>
      ) : null}

      <p className="mt-3 text-sm text-ink-muted break-words">
        Source: {measure.source_url ? (
          <a className="table-link" href={measure.source_url} target="_blank" rel="noopener">{measure.source_name}</a>
        ) : measure.source_name}
        , {measure.year}.{' '}
        <a className="table-link" href={`/charts/${measure.slug}`}>See every country</a>
      </p>

      {points.length ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-primary">The numbers</summary>
          <div className="mt-2 max-h-80 overflow-y-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th scope="col" className="text-left">Country</th>
                  <th scope="col" className="text-right">{measure.y_label}, {measure.year}</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.label}>
                    <td className="break-words">{p.label}</td>
                    <td className="text-right tabular-nums">{formatValue(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </div>
  )
}
